/**
 * Reset Password Page
 *
 * Allows users to set a new password using the reset token.
 */

import React, { FormEvent, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, ErrorMessage, Input, SuccessMessage } from '../components/AuthForm';

type ResetStatus = 'idle' | 'loading' | 'success' | 'error';

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState<ResetStatus>('idle');
  const [message, setMessage] = useState('');

  const isSubmitting = status === 'loading';
  const isTokenMissing = !token;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setMessage('');

    if (isTokenMissing) {
      setStatus('error');
      setMessage('Reset token is missing.');
      return;
    }

    if (password.length < 8) {
      setStatus('error');
      setMessage('Password must be at least 8 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setStatus('error');
      setMessage('Passwords do not match.');
      return;
    }

    try {
      setStatus('loading');
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.error || 'Failed to reset password.');
      }

      setStatus('success');
      setMessage(data?.data?.message || 'Password reset successful. You can now sign in.');
      setPassword('');
      setConfirmPassword('');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset password.';
      setStatus('error');
      setMessage(errorMessage);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--bg-color, #f9fafb)',
        padding: '20px',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '420px',
          backgroundColor: 'var(--card-bg, #ffffff)',
          borderRadius: '16px',
          padding: '40px',
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <h1
            style={{
              fontSize: '24px',
              fontWeight: 700,
              color: 'var(--text-color, #1f2937)',
              marginBottom: '8px',
            }}
          >
            Reset your password
          </h1>
          <p style={{ color: 'var(--text-muted, #6b7280)', fontSize: '14px' }}>
            Enter a new password to regain access to your account.
          </p>
        </div>

        {message && status === 'error' && (
          <div style={{ marginBottom: '16px' }}>
            <ErrorMessage message={message} />
          </div>
        )}

        {message && status === 'success' && (
          <div style={{ marginBottom: '16px' }}>
            <SuccessMessage message={message} />
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Input
              type="password"
              name="password"
              placeholder="New password"
              value={password}
              onChange={setPassword}
              required
              minLength={8}
              autoComplete="new-password"
            />
            <Input
              type="password"
              name="confirmPassword"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={setConfirmPassword}
              required
              minLength={8}
              autoComplete="new-password"
            />
            <Button type="submit" variant="primary" loading={isSubmitting} disabled={isTokenMissing}>
              Reset password
            </Button>
          </div>
        </form>

        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <Button variant="secondary" onClick={() => navigate('/signin')} disabled={isSubmitting}>
            Back to sign in
          </Button>
        </div>
      </div>
    </div>
  );
}
